import React from "react";
import {
  m as motion,
  MAnimatePresence as AnimatePresence,
} from "../../../shared/components/Motion";
import { formatCacheTime } from "shared/utils";
import {
  childVariants,
  accordionContainer,
  accordionWrapper,
  accordionItem,
} from "shared/utils";
import { ButtonComponent } from "shared/components";
import "./CacheInfoSection.css";

/**
 * CacheInfoSection Component
 * Displays cache information and provides cache clearing functionality
 */
const CacheInfoSection = ({
  isCacheInfoExpanded,
  setIsCacheInfoExpanded,
  cacheInfoMap,
  allSections,
  clearingCache,
  onClearCache,
  isAdmin,
}) => {
  return (
    <motion.div
      className="themes__section"
      initial="hidden"
      animate="visible"
      variants={childVariants}
      style={{
        background:
          "color-mix(in srgb, var(--title-color) 15%, var(--body-color))",
        padding: "0",
        gap: "0",
      }}
    >
      {/* Header with toggle */}
      <motion.div
        variants={childVariants}
        initial="hidden"
        animate="visible"
        onClick={() => setIsCacheInfoExpanded(!isCacheInfoExpanded)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "1.5rem",
          padding: "0.75rem",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          color: "var(--title-color)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "0.85rem",
            fontWeight: "600",
          }}
        >
          <i
            className={`bx bx-chevron-${
              isCacheInfoExpanded ? "down" : "right"
            }`}
          ></i>
          <span>Cache Info</span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            background: "var(--title-color)",
            color: "var(--body-color)",
            padding: "0.25rem 0.5rem",
            borderRadius: "4px",
            fontSize: "0.75rem",
            fontWeight: "600",
          }}
        >
          <i className="bx bx-time-five"></i>
          <span>24h Cache</span>
        </div>
      </motion.div>

      {/* Expanded content */}
      <AnimatePresence mode="wait">
        {isCacheInfoExpanded && (
          <motion.div
            variants={accordionContainer}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ overflow: "hidden" }}
          >
            <motion.div
              variants={accordionWrapper}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{
                gap: "0.5rem",
                display: "flex",
                flexDirection: "column",
                margin: "0 0.5rem",
              }}
            >
              {/* Cache Timestamps */}
              {cacheInfoMap.home && (
                <motion.div
                  className="glass__card"
                  variants={accordionItem}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{
                    fontSize: "0.65rem",
                    color: "var(--text-color)",
                    display: "grid",
                    gap: "0.25rem",
                    margin: "2px",
                    padding: "0.5rem",
                    background: "var(--body-color)",
                    borderRadius: "3px",
                  }}
                >
                  <div>
                    <strong>Next Refresh:</strong>{" "}
                    <span
                      style={{ color: "var(--title-color)", fontWeight: "600" }}
                    >
                      {formatCacheTime(cacheInfoMap.home.nextRefreshAt)}
                    </span>
                  </div>
                </motion.div>
              )}

              {/* Sections List with Remaining Time - Scrollable */}

              <motion.div
                variants={accordionItem}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  paddingLeft: "0.5rem",
                }}
              >
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: "600",
                    color: "var(--text-color)",
                  }}
                >
                  Sections List
                </span>
                <span
                  style={{
                    fontSize: "0.6rem",
                    color: "var(--text-color)",
                    opacity: 0.7,
                  }}
                >
                  ({allSections.length} total)
                </span>
              </motion.div>

              <div
                style={{
                  position: "relative",
                  gap: "0.25rem",
                  display: "flex",
                  flexDirection: "column",
                }}
                className={`cache-sections-scroll ${
                  isAdmin && "cache-sections-scroll-admin"
                }`}
              >
                {allSections.map((section, index) => {
                  const cache = cacheInfoMap[section.id];
                  const remainingColor = cache?.timeRemaining
                    ? cache.timeRemaining.startsWith("0h")
                      ? "#ef4444"
                      : cache.timeRemaining.startsWith("1h") ||
                          cache.timeRemaining.startsWith("2h")
                        ? "#f59e0b"
                        : "#10b981"
                    : "var(--text-color)";

                  return (
                    <motion.div
                      className="glass__card"
                      variants={childVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      key={section.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0.4rem 0.5rem",
                        margin: "2px",
                        background: "var(--body-color)",
                        borderRadius: "3px",
                        fontSize: "0.65rem",
                        gap: "0.4rem",
                        flex: "0 0 auto",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "color-mix(in srgb, var(--title-color) 20%, var(--body-color))",
                            borderRadius: "3px",
                            fontSize: "0.6rem",
                            fontWeight: "700",
                            color: "var(--title-color)",
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </span>
                        <span
                          style={{
                            fontWeight: "600",
                            color: "var(--title-color)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {section.label}
                        </span>
                      </div>
                      <span
                        style={{
                          color: remainingColor,
                          fontWeight: "700",
                          fontSize: "0.7rem",
                          flexShrink: 0,
                          minWidth: "45px",
                          textAlign: "right",
                        }}
                      >
                        {cache?.timeRemaining || "N/A"}
                      </span>
                    </motion.div>
                  );
                })}
              </div>

              {/* Clear Cache Button */}
              <motion.div
                variants={childVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "0.7rem",
                }}
              >
                <ButtonComponent
                  buttonTitle="Clear Cache & Refresh"
                  buttonText="Clear Cache & Refresh"
                  onClick={onClearCache}
                  disabled={clearingCache}
                  className={`cache-refresh-btn ${
                    clearingCache ? "loading" : ""
                  }`}
                >
                  {clearingCache ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity }}
                        style={{ display: "flex" }}
                      >
                        <i
                          className="bx bx-loader"
                          style={{ fontSize: "0.8rem" }}
                        ></i>
                      </motion.div>
                      <span>Clearing...</span>
                    </>
                  ) : (
                    <>
                      <i
                        className="bx bx-refresh"
                        style={{ fontSize: "0.8rem" }}
                      ></i>
                      <span>Clear Cache & Refresh</span>
                    </>
                  )}
                </ButtonComponent>
              </motion.div>

              {/* Footer note */}
              <div
                style={{
                  fontSize: "0.6rem",
                  color: "var(--text-color)",
                  opacity: 0.7,
                  textAlign: "center",
                  margin: "0.5rem 0",
                }}
              >
                📊 Firestore optimized • 96% quota reduction
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CacheInfoSection;
